/**
 * @file: RemoveMembersPanel.tsx
 *
 * WeChat-style multi-select "remove members" sheet. Lists the room's removable
 * members (owner-kick candidates — the owner/self/bot are already filtered out
 * by the caller), each with a checkbox; the 取消 / 移除 header reports the
 * picked row ids via `onConfirm`. A thin `BottomPanel` wrapper; selection state
 * is local and resets each time it opens.
 */

import { Typography } from '@euler/app/components/typography';
import { Avatar } from '@euler/components/Avatar';
import { BottomPanel } from '@euler/components/BottomPanel';
import { FontFamily } from '@euler/components/typography';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { ChatRoomActiveMember } from '@euler/model/chat';
import { Check } from 'lucide-react-native';
import { FC, memo, useCallback, useEffect, useState } from 'react';
import {
  ActivityIndicator,
  FlatList,
  ListRenderItem,
  StyleSheet,
  TouchableOpacity,
  View,
} from 'react-native';

export const RemoveMembersPanel: FC<{
  visible: boolean;
  members: ChatRoomActiveMember[];
  busy?: boolean;
  onClose: () => void;
  onConfirm: (memberRowIds: string[]) => void;
}> = memo(({ visible, members, busy, onClose, onConfirm }) => {
  const { rpx } = useResponsiveContext();
  const [selected, setSelected] = useState<Set<string>>(new Set());

  // Reset the selection whenever the sheet (re)opens.
  useEffect(() => {
    if (visible) setSelected(new Set());
  }, [visible]);

  const toggle = useCallback((id: string) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const renderItem: ListRenderItem<ChatRoomActiveMember> = useCallback(
    ({ item }) => (
      <MemberSelectRow
        member={item}
        checked={selected.has(item.id)}
        onPress={() => toggle(item.id)}
      />
    ),
    [selected, toggle],
  );

  const count = selected.size;
  const canConfirm = count > 0 && !busy;

  return (
    <BottomPanel visible={visible} onClose={onClose}>
      <View style={{ flex: 1, backgroundColor: '#fff' }}>
        {/* Header: 取消 / 移除成员 (N) / 移除 */}
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            height: rpx(56),
            paddingHorizontal: rpx(16),
            borderBottomWidth: StyleSheet.hairlineWidth,
            borderBottomColor: '#ececec',
            gap: rpx(8),
          }}
        >
          <TouchableOpacity
            onPress={onClose}
            hitSlop={{ top: 8, right: 8, bottom: 8, left: 8 }}
            style={{ paddingHorizontal: rpx(4) }}
          >
            <Typography.Text size={rpx(15)} color="#3b82f6">
              取消
            </Typography.Text>
          </TouchableOpacity>
          <Typography.Text
            size={rpx(16)}
            color="#111"
            style={{
              flex: 1,
              textAlign: 'center',
              fontFamily: FontFamily.NotoSans.Bold,
            }}
          >
            移除成员{count > 0 ? ` (${count})` : ''}
          </Typography.Text>
          <TouchableOpacity
            onPress={() => onConfirm([...selected])}
            disabled={!canConfirm}
            hitSlop={{ top: 8, right: 8, bottom: 8, left: 8 }}
            style={{
              paddingHorizontal: rpx(16),
              paddingVertical: rpx(6),
              borderRadius: rpx(999),
              backgroundColor: canConfirm ? '#dc2626' : '#e5e7eb',
            }}
          >
            {busy ? (
              <ActivityIndicator size="small" color="#fff" />
            ) : (
              <Typography.Text
                size={rpx(14)}
                color={count > 0 ? '#fff' : '#9ca3af'}
                style={{ fontFamily: FontFamily.NotoSans.Medium }}
              >
                移除
              </Typography.Text>
            )}
          </TouchableOpacity>
        </View>

        <FlatList
          data={members}
          keyExtractor={m => m.id}
          renderItem={renderItem}
          ItemSeparatorComponent={RowSeparator}
          keyboardShouldPersistTaps="handled"
          ListEmptyComponent={
            <View style={{ padding: rpx(40), alignItems: 'center' }}>
              <Typography.Text size={rpx(13)} color="#9ca3af">
                没有可移除的成员
              </Typography.Text>
            </View>
          }
        />
      </View>
    </BottomPanel>
  );
});

const MemberSelectRow: FC<{
  member: ChatRoomActiveMember;
  checked: boolean;
  onPress: () => void;
}> = memo(({ member, checked, onPress }) => {
  const { rpx } = useResponsiveContext();
  return (
    <TouchableOpacity
      activeOpacity={0.7}
      onPress={onPress}
      style={{
        flexDirection: 'row',
        alignItems: 'center',
        paddingHorizontal: rpx(16),
        paddingVertical: rpx(12),
        gap: rpx(12),
      }}
    >
      <View
        style={{
          width: rpx(20),
          height: rpx(20),
          borderRadius: rpx(10),
          borderWidth: rpx(1.5),
          borderColor: checked ? '#dc2626' : '#d1d5db',
          backgroundColor: checked ? '#dc2626' : 'transparent',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {checked ? <Check size={rpx(14)} color="#fff" strokeWidth={3} /> : null}
      </View>
      <Avatar
        uri={member.avatarUrl ?? undefined}
        name={member.displayName}
        textStyle={{
          fontFamily: FontFamily.NotoSans.Medium,
          fontSize: rpx(14),
          lineHeight: rpx(20),
        }}
        style={{ width: rpx(36), height: rpx(36), borderRadius: rpx(18) }}
      />
      <Typography.Text
        size={rpx(14)}
        color="#111"
        numberOfLines={1}
        style={{ flex: 1, fontFamily: FontFamily.NotoSans.Medium }}
      >
        {member.displayName}
      </Typography.Text>
    </TouchableOpacity>
  );
});

const RowSeparator = memo(() => (
  <View
    style={{
      height: StyleSheet.hairlineWidth,
      backgroundColor: '#f0f0f0',
      marginLeft: 16,
    }}
  />
));
